import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  GraduationCap,
  Tag,
  Newspaper,
  Video as VideoIcon,
  FileText,
  Users,
  Inbox,
  LogOut,
  ExternalLink,
} from "lucide-react";

const ALL_LINKS = [
  { to: "courses", label: "Courses", icon: GraduationCap, roles: ["admin"] },
  { to: "leads", label: "Leads", icon: Inbox, roles: ["admin"] },
  { to: "members", label: "Members", icon: Users, roles: ["admin"] },
  { to: "offers", label: "Offers", icon: Tag, roles: ["admin", "editor"] },
  { to: "worksheets", label: "Worksheets", icon: FileText, roles: ["admin", "editor"] },
  { to: "blogs", label: "Blogs", icon: Newspaper, roles: ["admin"] },
  { to: "videos", label: "Videos", icon: VideoIcon, roles: ["admin"] },
  { to: "users", label: "Team", icon: Users, roles: ["admin"] },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const links = ALL_LINKS.filter((l) => l.roles.includes(user?.role));

  async function doLogout() {
    await logout();
    nav("/admin/login");
  }

  return (
    <div
      data-testid="admin-layout"
      className="min-h-screen flex bg-[#F7F6FB] text-[#1F2547]"
    >
      <aside className="w-64 bg-white border-r border-[#1F2547]/8 flex flex-col">
        <div className="p-6 border-b border-[#1F2547]/8">
          <Link to="/" className="font-display text-xl font-black">
            Percept <span className="text-[#9F87D8]">Admin</span>
          </Link>
          <div className="text-[10px] uppercase font-bold tracking-[0.2em] text-[#5B6079] mt-1">
            {user?.role === "admin" ? "Administrator" : "Editor"}
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {links.map((l) => {
            const Icon = l.icon;
            return (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`admin-nav-${l.to}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl font-bold text-sm transition-colors ${
                    isActive
                      ? "bg-[#9F87D8] text-white"
                      : "text-[#1F2547] hover:bg-[#EFE9F7]"
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                {l.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="p-4 border-t border-[#1F2547]/8 space-y-2">
          <Link
            to="/"
            target="_blank"
            data-testid="admin-view-site-link"
            className="flex items-center gap-2 px-3 py-2 text-sm font-bold text-[#5B6079] hover:text-[#1F2547]"
          >
            <ExternalLink className="w-4 h-4" /> View site
          </Link>
          <button
            data-testid="admin-logout-btn"
            onClick={doLogout}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm font-bold text-red-600 hover:bg-red-50 rounded-xl"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>

        <div className="p-4 text-[10px] text-[#5B6079] font-medium truncate">
          {user?.email}
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        <div className="p-8 md:p-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
